if (!customElements.get('m-mini-product-card')) {
  customElements.define(
    'm-mini-product-card',
    class MMiniProductCard extends HTMLElement {
      constructor() {
        super();
        this.variants = [];
        this.selectedOptions = [];
        this.currentVariant = null;
        this.onClick = (event) => this.handleClick(event);
        this.onDropdownClick = (event) => this.handleDropdownClick(event);
        this.onDocumentClick = (event) => this.handleDocumentClick(event);
        this.onWindowUpdate = () => this.positionPortalDropdown();
      }

      connectedCallback() {
        this.variantJsonEl = this.querySelector('[data-m-mini-card-variants]');
        this.optionEls = Array.from(this.querySelectorAll('[data-m-mini-card-option]'));
        this.priceEl = this.querySelector('[data-m-mini-card-price]');
        this.currencyCode = this.dataset.currencyCode || 'USD';

        this.variants = this.getVariants();
        this.selectedOptions = this.getInitialSelectedOptions();
        this.currentVariant = this.getSelectedVariant();

        this.addEventListener('click', this.onClick);
        document.addEventListener('click', this.onDocumentClick);
        this.render();
      }

      getVariants() {
        try {
          return JSON.parse(this.variantJsonEl?.textContent || '[]');
        } catch (error) {
          console.error('MMiniProductCard: variant JSON error', error);
          return [];
        }
      }

      getInitialSelectedOptions() {
        const selectedVariantId = Number(this.dataset.selectedVariantId);
        const selectedVariant = this.variants.find((variant) => variant.id === selectedVariantId) || this.variants[0];

        return Array.from(selectedVariant?.options || []);
      }

      handleClick(event) {
        if (this.activeDropdownEl?.contains(event.target)) return;

        const toggle = event.target.closest('[data-m-mini-card-toggle]');
        if (toggle) {
          this.toggleDropdown(toggle.closest('[data-m-mini-card-option]'));
          return;
        }

        const valueButton = event.target.closest('[data-m-mini-card-value]');
        if (valueButton) {
          this.selectValue(valueButton);
        }
      }

      handleDropdownClick(event) {
        const valueButton = event.target.closest('[data-m-mini-card-value]');
        if (valueButton) {
          this.selectValue(valueButton);
        }
      }

      handleDocumentClick(event) {
        if (this.contains(event.target)) return;
        if (this.activeDropdownEl?.contains(event.target)) return;

        this.closeDropdowns();
      }

      selectValue(valueButton) {
        const optionEl = valueButton.closest('[data-m-mini-card-option]') || this.activeOptionEl;
        if (!optionEl) return;

        const optionIndex = Number(optionEl.dataset.optionPosition) - 1;
        this.selectedOptions[optionIndex] = valueButton.dataset.mMiniCardValue;
        this.currentVariant = this.getSelectedVariant();

        this.closeDropdown(optionEl);
        this.render();
        this.dispatchVariantChange();
      }

      getSelectedVariant() {
        return (
          this.variants.find((variant) => {
            return this.selectedOptions.every((option, index) => variant.options?.[index] === option);
          }) || null
        );
      }

      render() {
        this.optionEls.forEach((optionEl, optionIndex) => {
          const selectedValue = this.selectedOptions[optionIndex] || '';
          const selectedEl = optionEl.querySelector('[data-m-mini-card-selected]');

          if (selectedEl) selectedEl.textContent = selectedValue;

          optionEl.querySelectorAll('[data-m-mini-card-value]').forEach((button) => {
            const isActive = button.dataset.mMiniCardValue === selectedValue;
            button.classList.toggle('is-active', isActive);
          });
        });

        if (this.priceEl && this.currentVariant) {
          this.priceEl.textContent = this.formatMoney(this.currentVariant.price);
        }
      }

      toggleDropdown(optionEl) {
        if (!optionEl) return;

        const isOpen = optionEl.classList.contains('is-open');
        this.closeDropdowns();

        if (!isOpen) this.openDropdown(optionEl);
      }

      openDropdown(optionEl) {
        const dropdownEl = optionEl.querySelector('[data-m-mini-card-dropdown]');

        optionEl.classList.add('is-open');
        optionEl.querySelector('[data-m-mini-card-toggle]')?.setAttribute('aria-expanded', 'true');

        if (dropdownEl) {
          this.openPortalDropdown(optionEl, dropdownEl);
        }
      }

      closeDropdown(optionEl) {
        optionEl.classList.remove('is-open');
        optionEl.querySelector('[data-m-mini-card-toggle]')?.setAttribute('aria-expanded', 'false');

        if (optionEl === this.activeOptionEl) {
          this.closePortalDropdown();
        }
      }

      closeDropdowns() {
        this.querySelectorAll('[data-m-mini-card-option].is-open').forEach((optionEl) => {
          this.closeDropdown(optionEl);
        });
      }

      openPortalDropdown(optionEl, dropdownEl) {
        this.closePortalDropdown();

        const placeholder = document.createComment('m-mini-product-card-dropdown');

        dropdownEl.parentNode.insertBefore(placeholder, dropdownEl);
        document.body.appendChild(dropdownEl);

        this.activeOptionEl = optionEl;
        this.activeDropdownEl = dropdownEl;
        this.activeDropdownPlaceholder = placeholder;

        dropdownEl.classList.add('is-portal');
        dropdownEl.addEventListener('click', this.onDropdownClick);
        window.addEventListener('resize', this.onWindowUpdate);
        window.addEventListener('scroll', this.onWindowUpdate, true);

        this.positionPortalDropdown();
      }

      closePortalDropdown() {
        if (!this.activeDropdownEl) return;

        this.activeDropdownEl.classList.remove('is-portal');
        this.activeDropdownEl.removeAttribute('style');
        this.activeDropdownEl.removeEventListener('click', this.onDropdownClick);

        if (this.activeDropdownPlaceholder?.parentNode) {
          this.activeDropdownPlaceholder.parentNode.insertBefore(this.activeDropdownEl, this.activeDropdownPlaceholder);
          this.activeDropdownPlaceholder.remove();
        }

        window.removeEventListener('resize', this.onWindowUpdate);
        window.removeEventListener('scroll', this.onWindowUpdate, true);

        this.activeDropdownEl = null;
        this.activeOptionEl = null;
        this.activeDropdownPlaceholder = null;
      }

      positionPortalDropdown() {
        if (!this.activeOptionEl || !this.activeDropdownEl) return;

        const toggleEl = this.activeOptionEl.querySelector('[data-m-mini-card-toggle]');
        if (!toggleEl) return;

        const rect = toggleEl.getBoundingClientRect();
        const viewportPadding = 8;
        const dropdownWidth = rect.width;
        const left = Math.min(Math.max(rect.left, viewportPadding), window.innerWidth - dropdownWidth - viewportPadding);

        this.activeDropdownEl.style.width = `${dropdownWidth}px`;
        this.activeDropdownEl.style.left = `${left}px`;
        this.activeDropdownEl.style.maxHeight = '150px';

        const dropdownHeight = this.activeDropdownEl.offsetHeight || 0;
        let top = rect.top - dropdownHeight;

        if (top < viewportPadding) {
          top = rect.bottom;
        }

        this.activeDropdownEl.style.top = `${Math.max(top, viewportPadding)}px`;
      }

      dispatchVariantChange() {
        this.dispatchEvent(
          new CustomEvent('variant:change', {
            bubbles: true,
            detail: {
              variant: this.currentVariant,
              options: this.selectedOptions,
            },
          }),
        );
      }

      formatMoney(cents) {
        return new Intl.NumberFormat(document.documentElement.lang || 'en', {
          style: 'currency',
          currency: this.currencyCode,
          minimumFractionDigits: Number(cents) % 100 === 0 ? 0 : 2,
          maximumFractionDigits: Number(cents) % 100 === 0 ? 0 : 2,
        })
          .format((Number(cents) || 0) / 100)
          .replace(/(?:\s|&nbsp;)*kr\.(?=<|$)/gi, 'kr')
          .replace(/(?:\s|&nbsp;)+kr(?=<|$)/gi, 'kr');
      }

      disconnectedCallback() {
        this.closePortalDropdown();
        this.removeEventListener('click', this.onClick);
        document.removeEventListener('click', this.onDocumentClick);
      }
    },
  );
}
